/**
 * client/client.component.ts
 *
 * Per your request, this is now a much closer copy of user.component.ts's
 * header/sidebar chrome — zoom controls, theme picker, all reusing the same
 * ThemeService and the same click-handler logic verbatim, not reinvented.
 *
 * Two things deliberately still NOT copied, flagged rather than silently
 * dropped:
 *   - The notification bell. It's backed by a real notifications API
 *     (unread counts, mark-as-read, etc.) that doesn't exist for Client
 *     accounts — including the markup without that backend would just be a
 *     permanently-empty decoration, not a "copy" of anything functional.
 *   - <tour-step-template> / the guided-tour system. user.component.ts's own
 *     finishTour() is an empty stub — the tour system is already inert for
 *     org users too, so this would be dead markup requiring a whole extra
 *     module import (TourNgxBootstrapModule or similar) for zero visible
 *     effect. Not worth the added dependency.
 *
 * ThemeService is reused as-is (imported from user/services/ — it's
 * providedIn:'root', so it isn't scoped to UserModule) rather than
 * duplicated, on the same reasoning as reusing ProgramService: the
 * addTheme/getuserTheme calls are keyed by user_id generically, nothing in
 * them is org-specific. I haven't seen theme.service.ts's actual source
 * (wasn't in any upload) — I'm calling it with the exact same shape
 * user.component.ts already does, but flagging that as inferred-from-usage,
 * not confirmed against the file itself.
 */
import { Component, OnInit, Renderer2 } from '@angular/core';
import { Router } from '@angular/router';
import { getClientNavData } from './_nav';

import { AuthService } from '../services/auth.service';
import { ThemeService } from '../user/services/theme.service';

import { apiUrl } from '../../environments/environment';

@Component({
  selector: 'app-client',
  templateUrl: './client.component.html',
  styleUrls: ['./client.component.scss']
})
export class ClientComponent implements OnInit {

  public currentYear = new Date();
  public sidebarMinimized = false;
  public navItems: any[] = [];
  public headerItems: any[] = [];
  currentHead: any = {};

  name = '';
  userDetails: any;
  apiLink = apiUrl;

  // ── Theme picker — same list, same ids, same CSS files as user.component.ts ──
  themes = [
    { name: 'Default Theme', color: '#2e2c34', id: 'default' },
    { name: 'Midnight Blue Theme', color: '#0f042b', id: 'midnight-blue' },
    { name: 'Black Coffee Theme', color: '#1c0700', id: 'black-coffee' },
    { name: 'Black Red Theme', color: '#190000', id: 'black-red' },
    { name: 'Midnight Green Theme', color: '#001721', id: 'midnight-green' },
    { name: 'Dark Maroon Theme', color: '#26000e', id: 'dark-maroon' },
    { name: 'Deep Navy Theme', color: '#00152c', id: 'deep-navy' },
    { name: 'Dark Forest Green Theme', color: '#132001', id: 'dark-forest-green' },
    { name: 'Deep Maroon Theme', color: '#270007', id: 'deep-maroon' },
    { name: 'Deep Brownish Red Theme', color: '#200800', id: 'deep-brownish-red' },
    { name: 'Dark Coffee Theme', color: '#1f1500', id: 'dark-coffee' },
    { name: 'Dark Olive Theme', color: '#121500', id: 'dark-olive' },
    { name: 'Dark Cerulean Theme', color: '#002334', id: 'dark-cerulean' },
  ];
  currentTheme = '';
  zoomFactor = 1;

  constructor(
    private router: Router,
    private renderer: Renderer2,
    private themeService: ThemeService,
    public authService: AuthService,
  ) {
    const navData = getClientNavData();
    this.navItems = navData.navItems;
    this.headerItems = navData.headerItems;
  }

  async ngOnInit() {
    await this.getUserDetails();
    if (this.authService.isAuthenticated()) await this.chnekUsertheme();

    // Restore which header item was last selected, same as user.component.ts,
    // so a page refresh doesn't dump the sidebar back to the first item.
    let currentHead = localStorage.getItem('client_currentHead');
    if (currentHead && currentHead.length > 0) {
      this.currentHead = JSON.parse(currentHead);
      const restored = this.headerItems.find((h: any) => h.menu_id === this.currentHead.menu_id);
      if (restored) this.navItems = restored.children || [];
    }
  }

  async getUserDetails() {
    this.userDetails = await this.authService.getUserDetails();

    // Check if the client is still logged in / is actually a client — same
    // guard pattern user.component.ts and vadmin.component.ts both use.
    if (this.userDetails.relogin && this.userDetails.relogin == 1) {
      await this.authService.removeUserDetails();
      this.authService.trackAuthStatus();
    } else if (this.userDetails.type != 'client') {
      this.authService.trackAuthStatus();
    }

    this.name = this.userDetails?.user?.org_name || this.userDetails?.user?.reg_fname || '';
  }

  // Header item clicked → populate the sidebar from its children, same
  // mechanic as user.component.ts's getChildMenu(), simplified: no menu-DB
  // group/leaf distinction to worry about since this nav is hand-written
  // (see _nav.ts), so every header item's first child is always a real route.
  getChildMenu(item: any) {
    this.navItems = item.children && item.children.length > 0 ? item.children : [];
    this.currentHead = item;
    localStorage.setItem('client_currentHead', JSON.stringify(item));

    if (this.navItems.length > 0) {
      this.router.navigate([this.navItems[0].url]);
    } else if (item.url) {
      this.router.navigate([item.url]);
    }
  }

  toggleMinimize(e: any) {
    this.sidebarMinimized = e;
  }

  async logout() {
    localStorage.removeItem('client_currentHead');
    await this.authService.removeUserDetails();
    this.authService.trackAuthStatus();
  }

  // ── Theme — copied verbatim from user.component.ts's toogleBlackTheme /
  // loadStyle / chnekUsertheme, just renamed nothing, changed nothing ──────
  toogleBlackTheme() {
    if (this.currentTheme == 'black-theme') {
      this.loadStyle('default');
    } else {
      this.loadStyle('black-theme');
    }
  }

  async loadStyle(styleName: string) {
    const head = document.getElementsByTagName('head')[0];

    const existingStyle = document.getElementById('client-theme');
    if (existingStyle) {
      head.removeChild(existingStyle);
    }

    const style = document.createElement('link');
    if (styleName && styleName.length > 0) {
      style.id = 'client-theme';
      style.rel = 'stylesheet';
      style.href = 'assets/themes/' + styleName + '.css';
    } else {
      style.id = 'client-theme';
      style.rel = 'stylesheet';
      style.href = 'assets/themes/default.css';
    }
    head.appendChild(style);

    let data = {
      user_id: this.userDetails.user.id,
      theme_name: styleName
    };
    await this.themeService.addTheme(data);
    this.currentTheme = styleName;
  }

  async chnekUsertheme() {
    let data = {
      user_id: this.userDetails.user.id,
    };
    let getTheme: any;
    try {
      getTheme = await this.themeService.getuserTheme(data);
      const head = document.getElementsByTagName('head')[0];

      const existingStyle = document.getElementById('client-theme');
      if (existingStyle) {
        head.removeChild(existingStyle);
      }

      const style = document.createElement('link');
      style.id = 'client-theme';
      style.rel = 'stylesheet';

      let themeId = '';
      if (getTheme && getTheme.length > 0 && getTheme[0].theme_name && getTheme[0].theme_name.length > 0) {
        style.href = 'assets/themes/' + getTheme[0].theme_name + '.css';
        themeId = getTheme[0].theme_name;
      } else {
        style.href = 'assets/themes/default.css';
        themeId = 'default';
      }
      head.appendChild(style);
      this.currentTheme = themeId;
    } catch (err) {
      // Same fallback shape as user.component.ts: if the theme lookup fails
      // for any reason, still fall back to the default stylesheet so the
      // shell isn't left completely unstyled the way it was before this fix.
      const head = document.getElementsByTagName('head')[0];
      const existingStyle = document.getElementById('client-theme');
      if (existingStyle) head.removeChild(existingStyle);
      const style = document.createElement('link');
      style.id = 'client-theme';
      style.rel = 'stylesheet';
      style.href = 'assets/themes/default.css';
      head.appendChild(style);
      this.currentTheme = 'default';
    }
  }

  // ── Zoom — copied verbatim from user.component.ts ───────────────────────
  zoomIn() {
    if (this.zoomFactor < 1.5) {
      this.zoomFactor += 0.1;
      this.applyZoom();
    }
  }
  zoomOut() {
    if (this.zoomFactor > 0.5) {
      this.zoomFactor -= 0.1;
      this.applyZoom();
    }
  }
  resetFontSize() {
    this.zoomFactor = 1;
    this.applyZoom();
  }
  private applyZoom() {
    this.renderer.setStyle(document.documentElement, 'font-size', `${this.zoomFactor * 100}%`);
  }
}
